import React from 'react';
import FirstComparisonControl from 'utils/components/FiltersModal/comparison/FirstComparisonControl';
import SecondComparisonControl from 'utils/components/FiltersModal/comparison/SecondComparisonControl';
import {
    Comparison,
    isRangeComparisonValid,
    RangeComparison,
    SupportedComparisonValue,
} from 'utils/components/FiltersModal/comparison/FilterComparison';
import {
    BoundarySign,
    ExactSign,
    getOppositeBoundarySign,
    getSupportedOppositeBoundarySigns,
} from 'utils/filter/filter';
import { ValueType } from '@instinet-analytics/semantic-ui-components';

export interface ComparisonGroupRangeProps {
    columnName: string;
    comparison: RangeComparison;
    setComparison?: (comparison: Comparison, commitOnSet?: boolean) => void;
    onInputEnter: () => void;
}

export default function ComparisonGroupRange(props: ComparisonGroupRangeProps) {
    const { columnName, comparison, setComparison, onInputEnter } = props;

    if (!comparison || !setComparison) {
        return null;
    }

    const [firstComparison, secondComparison] = comparison;

    const rangeError =
        (!!firstComparison.value || !!secondComparison.value) &&
        !isRangeComparisonValid(comparison);

    const onSignBecomeExact = (sign: ExactSign) =>
        setComparison({
            valueType: firstComparison.valueType,
            sign,
            value: firstComparison.value,
        });

    const onSignBecomeBoundary = (sign: BoundarySign) =>
        setComparison([
            {
                ...firstComparison,
                sign,
            },
            {
                ...secondComparison,
                sign:
                    secondComparison.sign &&
                    getSupportedOppositeBoundarySigns(sign).includes(
                        secondComparison.sign
                    )
                        ? secondComparison.sign
                        : getOppositeBoundarySign(sign),
            },
        ]);

    const onFirstValueChange = (value: SupportedComparisonValue) => {
        if (
            (firstComparison.valueType === ValueType.DATE ||
                firstComparison.valueType === ValueType.DATETIME) &&
            value === 'Today'
        ) {
            secondComparison.sign = undefined;
            secondComparison.value = undefined;
        }

        setComparison([
            {
                ...firstComparison,
                value,
            },
            secondComparison,
        ]);
    };

    const onSecondSignChange = (sign: BoundarySign) =>
        setComparison([
            firstComparison,
            {
                ...secondComparison,
                sign,
            },
        ]);

    const onSecondValueChange = (value: SupportedComparisonValue) =>
        setComparison([
            firstComparison,
            {
                ...secondComparison,
                value,
            },
        ]);

    return (
        <>
            <FirstComparisonControl
                columnName={columnName}
                comparison={firstComparison}
                onSignBecomeExact={onSignBecomeExact}
                onSignBecomeBoundary={onSignBecomeBoundary}
                onValueChange={onFirstValueChange}
                onInputEnter={onInputEnter}
            />

            <SecondComparisonControl
                columnName={columnName}
                firstSign={firstComparison.sign!}
                secondComparison={secondComparison}
                onSignChange={onSecondSignChange}
                onValueChange={onSecondValueChange}
                rangeError={rangeError}
                onInputEnter={onInputEnter}
            />
        </>
    );
}
